import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Settings, FileJson, AlertCircle, Zap, ListChecks } from 'lucide-react';
import type { StreamStats, ServerStats, EncoderConfig, EncoderConfigField, PerEngineData, EncoderCatalogEntry, EncoderId } from '../../types';
import { formatHostRelay } from '../../utils/relayDisplay';
import { dumpTriggerService } from '../../core/DumpTriggerService';

// reason_code → 中文（与 SettingsPanel.tsx 内的一致；后续可抽公共文件）
function reasonText(code: string): string {
  switch (code) {
    case 'no_dll':              return 'DLL 未安装';
    case 'init_failed':         return '初始化失败';
    case 'session_failed':      return 'GPU 会话创建失败';
    case 'codec_not_supported': return '此编码器不被 GPU 支持';
    case 'encode_failed':       return '编码测试失败';
    case 'timeout':             return '自检超时';
    case 'cores_too_few':       return 'CPU 核心数不足';
    case 'disabled_by_env':     return '已被环境变量禁用';
    case 'probe_pending':       return '自检进行中';
    case 'runtime_fallback':    return '运行期已降级';
    case 'slow_perf_warn':      return '性能不足';
    default:                    return code || '不可用';
  }
}

function encoderDisplayName(id: EncoderId): string {
  switch (id) {
    case 'nvenc_av1':     return 'NVENC AV1';
    case 'nvenc_h265':    return 'NVENC H.265';
    case 'nvenc_h264':    return 'NVENC H.264';
    case 'libaom_av1':    return 'AV1 (libaom)';
    case 'x264_h264':     return 'x264';
    case 'openh264_h264': return 'OpenH264';
    default:              return id;
  }
}

interface BottomDrawerProps {
  stats: StreamStats;
  serverStats: ServerStats | null;
}

interface DrawerSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  disabled?: boolean;
  hint?: string;
  children?: React.ReactNode;
}

const DrawerSection: React.FC<DrawerSectionProps> = ({ title, icon, defaultOpen, disabled, hint, children }) => {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`w-full flex items-center gap-2 px-4 py-2.5 transition-colors ${
          disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'
        }`}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-zinc-400">{icon}</span>
        <span className="text-xs font-bold tracking-wide text-zinc-300 uppercase flex-1 text-left">
          {title}
        </span>
        {hint && <span className="text-[10px] text-zinc-500">{hint}</span>}
      </button>
      {open && !disabled && (
        <div className="px-4 py-3 border-t border-white/5 bg-black/20">
          {children}
        </div>
      )}
    </div>
  );
};

const BottomDrawer: React.FC<BottomDrawerProps> = ({ stats, serverStats }) => {
  const sv = serverStats;
  const hostRelay = formatHostRelay(sv);

  // 主机基础信息（host/分辨率/CPU/GPU 等，不属于编码器内部参数）
  const hostInfoContent = sv ? (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11px] font-mono">
      <div className="flex justify-between"><span className="text-zinc-500">编码器</span><span className="text-zinc-300">{sv.encoder_name}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">类型</span><span className="text-zinc-300">{sv.encoder_type}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">分辨率</span><span className="text-zinc-300">{sv.width}×{sv.height}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">目标 FPS</span><span className="text-zinc-300">{sv.target_fps}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">采集模式</span><span className="text-zinc-300">{sv.capture_mode}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">显示模式</span><span className={sv.display_mode === 'virtual' ? 'text-amber-400' : sv.display_mode === 'none' ? 'text-red-400 font-bold' : 'text-zinc-300'}>{sv.display_mode === 'virtual' ? '虚拟显示器' : sv.display_mode === 'none' ? '无显示器(降级)' : sv.display_mode === 'physical' ? '物理显示器' : '-'}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">GPU</span><span className="text-zinc-300">{sv.gpu_name}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">CPU</span><span className="text-zinc-300">{sv.cpu_model || 'Unknown'}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">CPU 占用</span><span className="text-zinc-300">{sv.cpu_usage ?? '-'}%</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">平台</span><span className="text-zinc-300">{sv.platform || '-'}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">主用 engine</span><span className="text-zinc-300 font-bold">{sv.dominant_engine || '-'}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">AV1 硬编</span><span className="text-zinc-300">{sv.av1_hw ? '支持' : '不支持'}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">Host 中继</span><span className="text-zinc-300 truncate max-w-[260px]">{hostRelay || '-'}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">Host 路径</span><span className="text-zinc-300">{sv.host_active_path || '-'}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">Host 会话</span><span className="text-zinc-300 truncate max-w-[180px]">{sv.host_active_session || '-'}</span></div>
    </div>
  ) : (
    <div className="text-[11px] text-zinc-500">未连接</div>
  );

  // 编码器内部参数（per-engine encoder_config，Stage 3 后续完整支持）
  const dominant = sv?.dominant_engine || '';
  const encoderConfig = sv?.per_engine?.[dominant]?.encoder_config;
  const ed = sv?.per_engine?.[dominant];

  const encoderConfigContent = encoderConfig ? (
    <EncoderConfigPanel
      config={encoderConfig}
      runtimeFields={ed ? buildRuntimeFields(ed) : []}
    />
  ) : sv ? (
    <div className="text-[11px] text-zinc-500">
      当前编码器（{dominant}）配置数据未加载（host 旧版？或编码器尚未 init）
    </div>
  ) : (
    <div className="text-[11px] text-zinc-500">未连接</div>
  );

  // 原始 JSON 内容
  const rawJson = JSON.stringify({ stats, serverStats }, null, 2);

  // 编码器自检报告（阶段 2）：catalog 全量 + 失败 reason
  const catalog: EncoderCatalogEntry[] | undefined = sv?.encoder_catalog;
  const activeId = sv?.active_encoder_id;
  const catalogContent = catalog && catalog.length > 0 ? (
    <div className="space-y-1.5">
      {catalog.map((e) => {
        const isActive = activeId === e.id;
        return (
          <div key={e.id} className="flex items-center justify-between text-[11px] font-mono py-1 px-2 rounded bg-black/20">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                e.available ? 'bg-green-400' : 'bg-zinc-600'
              }`} />
              <span className={`${e.available ? 'text-zinc-200' : 'text-zinc-500 line-through'} truncate`}>
                {encoderDisplayName(e.id)}
              </span>
              <span className="text-[9px] text-zinc-500 flex-shrink-0">{e.accel}</span>
              {isActive && <span className="text-[9px] text-blue-400 flex-shrink-0">[当前]</span>}
              {e.slow_perf_warn && (
                <span className="text-[9px] text-amber-400 flex-shrink-0" title="probe 时单帧编码 >16ms，跑 60fps 可能吃力">⚠ 性能</span>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 flex-shrink-0 ml-2">
              {e.available
                ? `init ${e.probe_init_ms}ms · encode ${e.probe_encode_ms}ms`
                : reasonText(e.reason_code)}
            </div>
          </div>
        );
      })}
      {sv?.probe_pending && (
        <div className="text-[10px] text-amber-400 italic">⏳ 自检仍在进行中，结果会自动刷新</div>
      )}
    </div>
  ) : sv ? (
    <div className="text-[11px] text-zinc-500">主机未推送编码器 catalog（旧版 host？）</div>
  ) : (
    <div className="text-[11px] text-zinc-500">未连接</div>
  );

  return (
    <div className="space-y-2">
      <DrawerSection
        title="主机基础信息"
        icon={<Settings size={14} />}
        defaultOpen={false}
      >
        {hostInfoContent}
      </DrawerSection>

      <DrawerSection
        title="编码器自检报告"
        icon={<ListChecks size={14} />}
        defaultOpen={false}
        hint={catalog ? `${catalog.filter(e => e.available).length}/${catalog.length} 可用` : undefined}
      >
        {catalogContent}
      </DrawerSection>

      <DrawerSection
        title={`编码器内部参数（${dominant || '—'}）`}
        icon={<Settings size={14} />}
        defaultOpen={false}
        hint={encoderConfig ? `${encoderConfig.fields.length} 项配置` : undefined}
      >
        {encoderConfigContent}
      </DrawerSection>

      <DrawerSection
        title="原始 JSON"
        icon={<FileJson size={14} />}
        hint={`${rawJson.length} 字符`}
      >
        <pre className="text-[10px] text-zinc-400 font-mono overflow-x-auto custom-scrollbar max-h-[300px] overflow-y-auto whitespace-pre-wrap break-all">
          {rawJson}
        </pre>
      </DrawerSection>

      {/* Stage 3D: 5 分钟历史已经在顶栏"最近 5 分钟"切换实现，删除原占位 */}

      {/* Stage 3E: 触发式 dump 开关 */}
      <AutoDumpToggle />
    </div>
  );
};

// Stage 3E: 触发式 dump 开关组件
// 初始 state 从 service 单例读（确保 Dashboard 关闭重开后开关状态保留）
const AutoDumpToggle: React.FC = () => {
  const [enabled, setEnabled] = useState(() => dumpTriggerService.isAutoDownloadEnabled());
  const [testStatus, setTestStatus] = useState('');
  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    dumpTriggerService.setAutoDownload(next);
  };
  const handleTest = () => {
    // 临时启用 autoDownload 让测试触发能下载文件，再恢复原状态
    const wasEnabled = enabled;
    if (!wasEnabled) dumpTriggerService.setAutoDownload(true);
    const ok = dumpTriggerService.testFire();
    if (!wasEnabled) dumpTriggerService.setAutoDownload(false);
    setTestStatus(ok ? '✓ 已下载测试快照' : '✗ 还没收到 stats');
    setTimeout(() => setTestStatus(''), 3000);
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center gap-3">
      <AlertCircle size={14} className="text-zinc-400" />
      <div className="flex-1">
        <div className="text-xs font-bold tracking-wide text-zinc-300 uppercase">触发式 Dump</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">
          冻结 / BWE 突变 / CPU 过载 / 编码器重建 时自动下载 JSON 快照（30s 防风暴）
        </div>
        {testStatus && <div className="text-[10px] text-emerald-400 mt-0.5">{testStatus}</div>}
      </div>
      <button
        onClick={handleTest}
        title="立即触发一次测试 dump（验证流程）"
        className="px-3 py-1 rounded-full text-[10px] font-bold bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 transition-colors"
      >
        测试触发
      </button>
      <button
        onClick={handleToggle}
        className={`px-3 py-1 rounded-full text-[10px] font-bold transition-colors ${
          enabled
            ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300'
            : 'bg-white/5 border border-white/10 text-zinc-400 hover:bg-white/10'
        }`}
      >
        {enabled ? '已启用' : '已禁用'}
      </button>
    </div>
  );
};

// ===== Stage 3 后续: EncoderConfigPanel =====
// 显示每个 engine 的 encoder_config，分为：
//   1. 静态字段：host 显式设置（[HOST] 标蓝） + 关键默认值（[默认] 标灰）
//   2. 运行时字段：⚠️ 标注，hover 显示变化条件
//   3. opaque_note：libaom/openh264 内部参数说明

interface EncoderConfigPanelProps {
  config: EncoderConfig;
  runtimeFields: EncoderConfigField[];   // 从 PerEngineData 派生的运行时字段
}

const EncoderConfigPanel: React.FC<EncoderConfigPanelProps> = ({ config, runtimeFields }) => {
  const [showDefaults, setShowDefaults] = useState(true);

  const hostFields = config.fields.filter(f => f.src === 'host');
  const defaultFields = config.fields.filter(f => f.src === 'default');

  return (
    <div className="space-y-3">
      {/* opaque_note (libaom/openh264) */}
      {config.opaque_note && (
        <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-2 text-[10px] text-blue-300">
          ℹ️ {config.opaque_note}
        </div>
      )}

      {/* 静态字段 */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-[10px] text-zinc-400 uppercase tracking-wide font-bold">
            启动时配置（{hostFields.length} 项 host 修改 / {defaultFields.length} 项默认）
          </span>
          <label className="flex items-center gap-1 text-[10px] text-zinc-500 cursor-pointer">
            <input
              type="checkbox"
              checked={showDefaults}
              onChange={(e) => setShowDefaults(e.target.checked)}
              className="cursor-pointer"
            />
            显示默认值
          </label>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono">
          {hostFields.map((f, i) => <ConfigFieldRow key={`h${i}`} field={f} />)}
          {showDefaults && defaultFields.map((f, i) => <ConfigFieldRow key={`d${i}`} field={f} />)}
        </div>
      </div>

      {/* 运行时字段 */}
      {runtimeFields.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Zap size={11} className="text-amber-400" />
            <span className="text-[10px] text-amber-400 uppercase tracking-wide font-bold">
              运行时参数（自适应，{runtimeFields.length} 项）
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono">
            {runtimeFields.map((f, i) => <ConfigFieldRow key={`r${i}`} field={f} runtime />)}
          </div>
        </div>
      )}

      {/* runtime_note */}
      {config.runtime_note && (
        <div className="text-[10px] text-zinc-500 italic">
          {config.runtime_note}
        </div>
      )}
    </div>
  );
};

const ConfigFieldRow: React.FC<{ field: EncoderConfigField; runtime?: boolean }> = ({ field, runtime }) => {
  const valueColor = runtime
    ? 'text-amber-300'
    : field.src === 'host'
    ? 'text-zinc-200'
    : 'text-zinc-500 italic';
  const tagColor = runtime
    ? 'text-amber-500 bg-amber-500/10 border-amber-500/30'
    : field.src === 'host'
    ? 'text-blue-400 bg-blue-500/10 border-blue-500/30'
    : 'text-zinc-600 bg-white/5 border-white/10';
  // 仅 emoji（hover 看 tooltip 解释）
  // ✏️ host 显式设置 / 📦 编码器 preset 默认（出厂未动）/ ⚠️ 运行时自适应
  const tagText = runtime ? '⚠️' : field.src === 'host' ? '✏️' : '📦';
  const tooltip = field.note
    || (runtime ? '运行时随 BWE/网络变化（自适应）' : field.src === 'host' ? '由 host 代码显式设置（webrtc_host 调 NVENC/FFmpeg API）' : '编码器/preset 默认行为（host 没改这个字段）');

  return (
    <div className="flex justify-between items-baseline group" title={tooltip}>
      <span className="text-zinc-500 truncate" title={field.k}>{field.k}</span>
      <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
        <span className={valueColor}>{field.v}</span>
        <span
          className={`text-[11px] leading-none cursor-help select-none`}
          aria-label={tooltip}
        >
          {tagText}
        </span>
      </div>
    </div>
  );
};

// 从 PerEngineData 派生运行时字段（vbv_buf_kbits/vbv_max_rate_kbps/vbv_multiplier 等）
function buildRuntimeFields(ed: PerEngineData): EncoderConfigField[] {
  const fields: EncoderConfigField[] = [];

  if (ed.vbv_buf_kbits > 0 || ed.vbv_max_rate_kbps > 0) {
    fields.push({
      k: '目标码率',
      v: `${ed.vbv_max_rate_kbps} kbps`,
      src: 'host',  // 用 src 占位，但 runtime 标记由 ConfigFieldRow.runtime prop 决定
      note: '随 BWE 实时变化。BitrateController MIN 聚合 + EMA（α=0.5 降 / α=0.1 升）',
    });
    fields.push({
      k: 'VBV 缓冲',
      v: `${ed.vbv_buf_kbits} kb`,
      src: 'host',
      note: 'NVENC: bitrate/fps × 3 帧。x264: per_frame_budget × multiplier，封顶 1500kb',
    });
  }

  if (ed.vbv_multiplier !== undefined && ed.vbv_multiplier > 0) {
    fields.push({
      k: 'VBV multiplier',
      v: ed.vbv_multiplier,
      src: 'host',
      note: '仅 x264。log 映射 BWE→multiplier (500→2 / 1500→11 / 3000→16)。sudden_drop 触发软减半',
    });
  }

  if (ed.vbv_capped) {
    fields.push({
      k: 'VBV cap',
      v: '触发',
      src: 'host',
      note: '仅 x264。raw_bufsize > 1500kb 触发，单帧封顶',
    });
  }

  if (ed.last_qp !== undefined && ed.last_qp >= 0) {
    fields.push({
      k: '当前 QP',
      v: ed.last_qp,
      src: 'host',
      note: '最近一帧编码后的平均 QP。NVENC AV1 范围 0-255、其他 0-51',
    });
  }

  if (ed.nvenc_gdr?.intra_refresh_active) {
    fields.push({
      k: 'GDR 进度',
      v: `${ed.nvenc_gdr.progress_pct}%（${ed.nvenc_gdr.frame_in_period}/${ed.nvenc_gdr.period_frames}帧）`,
      src: 'host',
      note: 'Intra Refresh 周期内进度，0-99% 循环。period = fps × 8 帧',
    });
  }

  if (ed.nvenc_reconfig?.count_total !== undefined && ed.nvenc_reconfig.count_total > 0) {
    fields.push({
      k: 'NVENC reconfig',
      v: `${ed.nvenc_reconfig.count_total} 次（失败 ${ed.nvenc_reconfig.failure_count_total}）`,
      src: 'host',
      note: 'nvEncReconfigureEncoder 调用次数。failure > 0 即异常（历史 bug 监测）',
    });
  }

  fields.push({
    k: '活跃订阅者',
    v: ed.active_subscriber_count,
    src: 'host',
    note: 'ICE Racing 时多 PC 共享编码会话',
  });

  return fields;
}

export default BottomDrawer;
